import { useState, useEffect } from 'react';

/**
 * Hook to detect viewport size and orientation
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Hook to determine dock position based on viewport
 * - Mobile portrait: bottom
 * - Mobile landscape: left
 * - Desktop: left
 */
export function useDockPosition(): 'bottom' | 'left' {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Mobile portrait = bottom, everything else = left
  if (isMobile && isPortrait) {
    return 'bottom';
  }
  return 'left';
}

/**
 * Common breakpoint hooks
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 768px)');
}

export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
}

export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1025px)');
}

export function useIsLandscape(): boolean {
  return useMediaQuery('(orientation: landscape)');
}
